'use client'

import { useCallback, useState, type ReactNode } from 'react'
import { Announcer } from './Announcer'
import { AnnounceContext, AnnouncementContext } from './context'
import {
  EMPTY_ANNOUNCEMENT,
  nextAnnouncement,
  type Announce,
  type Politeness,
} from './state'

// Mounted once at the app root. Outside it, announce() is a no-op and Announcer renders
// empty regions, so components can render in tests without wiring it up.
export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [announcement, setAnnouncement] = useState(EMPTY_ANNOUNCEMENT)

  const announce = useCallback<Announce>((text, { assertive = false, source } = {}) => {
    const politeness: Politeness = assertive ? 'assertive' : 'polite'
    // Deferred past the current commit so an Announcer that mounts in the same event — a
    // popover opening on the very click that announces — is already in place to carry it.
    setTimeout(() => {
      setAnnouncement((previous) =>
        nextAnnouncement(previous, text, politeness, Date.now(), source)
      )
    }, 0)
  }, [])

  return (
    <AnnounceContext.Provider value={announce}>
      <AnnouncementContext.Provider value={announcement}>
        <Announcer />
        {children}
      </AnnouncementContext.Provider>
    </AnnounceContext.Provider>
  )
}
